import React from 'react';
import {
  ViewButton,
  EditButton,
  TestQueryButton,
  GoogleSpreadsheetButton,
} from '../Common/DataTable/tableActionButtons';
import { getFilter, getLabel, getSort } from '../Common/DataTable/tableHelpers';
import moment from 'moment';
import sendLeadTypes from '../../config/sendLeadTypes';

const createTableColumns = (
  customers,
  sales,
  openViewModal,
  openEditModal,
  openTestQueryModal,
  messageTemplates = [],
  aiModule = false,
) => {
  // `active` in the store is this same window measured when the listener read
  // (store/monetizationOrders/saga.js): this is measured at render, and says
  // which side of the window a closed order sits on.
  const windowState = (row) => {
    const now = moment();
    if (row.startDate && now.isBefore(moment(row.startDate)))
      return 'scheduled';
    if (row.endDate && now.isAfter(moment(row.endDate))) return 'expired';
    return 'active';
  };

  const aiColumns = !aiModule
    ? []
    : [
        {
          title: 'AI',
          name: 'ai',
          width: 170,
          // Off, the offer does not exist for the agent: never proposed and
          // never closed.
          getCellValue: (row) =>
            !row.aiEnabled
              ? 'OFF'
              : row.aiClosureAllowed === false
              ? 'ON · needs a person'
              : 'ON · closes alone',
          formatterComponent: ({ value }) =>
            value === 'OFF' ? (
              <span className="badge badge-dark font-size-12">OFF</span>
            ) : (
              <div>
                <span className="badge badge-success font-size-12">ON</span>
                <div className="text-muted font-size-12">
                  {value.replace('ON · ', '')}
                </div>
              </div>
            ),
        },
        {
          title: 'AI can close',
          name: 'aiClosureAllowed',
          width: 110,
          getCellValue: (row) =>
            !row.aiEnabled ? '' : row.aiClosureAllowed === false ? 'no' : 'yes',
          hidden: true,
        },
        {
          title: 'AI texts complete?',
          name: 'aiTexts',
          width: 140,
          // The form requires them when aiEnabled, so a gap here is an order
          // saved before that rule or written by a script.
          getCellValue: (row) => {
            if (!row.aiEnabled) return '';
            const missing = [
              !row.aiPitch && 'pitch',
              !row.aiQualificationCriteria && 'criteria',
              row.aiClosureAllowed === false
                ? !row.aiProposalMessage && 'proposal'
                : !row.aiClosingMessage && 'closing',
            ].filter(Boolean);
            return missing.length ? `missing: ${missing.join(', ')}` : 'yes';
          },
          hidden: true,
        },
      ];

  return [
    {
      title: 'Name',
      name: 'name',
      width: 240,
    },
    {
      title: 'Customer',
      name: 'customerId',
      getCellValue: (row) => getLabel(row.customerId, customers),
    },
    {
      title: 'Status',
      name: 'status',
      width: 110,
      getCellValue: (row) => windowState(row),
      formatterComponent: ({ value }) =>
        value === 'active' ? (
          <span className="badge badge-success font-size-12">ACTIVE</span>
        ) : value === 'scheduled' ? (
          <span className="badge badge-info font-size-12">SCHEDULED</span>
        ) : (
          <span className="badge badge-secondary font-size-12">EXPIRED</span>
        ),
    },
    {
      title: 'Start Date',
      name: 'startDate',
      width: 120,
      formatterComponent: ({ value }) => moment(value).format('DD/MM/YYYY'),
      sorting: (a, b) => getSort(moment(a).valueOf(), moment(b).valueOf()),
      filter: (value, filter) =>
        getFilter(moment(value).format('DD/MM/YYYY'), filter.value),
      exportValue: ({ value }) => moment(value).format('DD/MM/YYYY'),
    },
    {
      title: 'End Date',
      name: 'endDate',
      width: 120,
      formatterComponent: ({ value }) => moment(value).format('DD/MM/YYYY'),
      sorting: (a, b) => getSort(moment(a).valueOf(), moment(b).valueOf()),
      filter: (value, filter) =>
        getFilter(moment(value).format('DD/MM/YYYY'), filter.value),
      exportValue: ({ value }) => moment(value).format('DD/MM/YYYY'),
    },
    {
      title: 'Days left',
      name: 'daysLeft',
      width: 100,
      getCellValue: (row) =>
        row.endDate ? moment(row.endDate).diff(moment(), 'days') : '',
      formatterComponent: ({ value }) =>
        value === '' || value < 0 ? '—' : `${value}`,
      filter: 'disabled',
    },
    {
      title: 'Priority',
      name: 'priority',
      width: 90,
      // It orders the offers of one acquisition order between themselves, and
      // the agent proposes them in this order.
      getCellValue: (row) => row.priority || '',
    },
    ...aiColumns,
    {
      title: 'Goal / Valid',
      name: 'goalLeads',
      width: 150,
      // validLeads is typed by hand — no function writes it — so these two
      // numbers are a declaration, not a live count.
      getCellValue: (row) => {
        const goal = row.leads || 0;
        const valid = row.validLeads || 0;
        if (!goal && !valid) return '';
        const share = goal ? ` · ${Math.round((valid / goal) * 100)}%` : '';
        return `${goal} / ${valid}${share}`;
      },
    },
    {
      title: 'Payout',
      name: 'payout',
      width: 100,
      formatterComponent: ({ value }) => `€ ${value}`,
    },
    {
      title: 'Cost per positive closure',
      name: 'costPositiveClosure',
      width: 100,
      formatterComponent: ({ value }) => `€ ${value}`,
    },
    {
      title: 'Sale',
      name: 'supplierId',
      getCellValue: (row) => getLabel(row.supplierId, sales),
      hidden: true,
    },
    {
      title: 'How send',
      name: 'sendLeadType',
      width: 140,
      getCellValue: (row) => {
        const type = sendLeadTypes.find(
          ({ value }) => value === row.sendLeadType,
        );
        return type ? type.label : row.sendLeadType || '';
      },
      hidden: true,
    },
    {
      title: 'Spreadsheet',
      name: 'googleSpreadsheetId',
      width: 120,
      // `processing` is what hides the edit button in the action column: say
      // so, instead of leaving an operator wondering.
      getCellValue: (row) => {
        const id = row.googleSpreadsheetId;
        if (!id) return '';
        return ['new', 'processing', 'error'].includes(id) ? id : 'ready';
      },
      formatterComponent: ({ value }) =>
        value === 'error' ? (
          <span className="badge badge-danger font-size-12">ERROR</span>
        ) : value === 'processing' ? (
          <span className="badge badge-warning font-size-12">PROCESSING</span>
        ) : value === 'ready' ? (
          <span className="badge badge-success font-size-12">READY</span>
        ) : value === 'new' ? (
          <span className="badge badge-info font-size-12">NEW</span>
        ) : (
          ''
        ),
      hidden: true,
    },
    {
      title: 'Send schedule',
      name: 'sendSchedule',
      width: 110,
      getCellValue: (row) => (row.sendSchedule?.enabled ? 'yes' : 'no'),
      hidden: true,
    },
    {
      title: 'Web service',
      name: 'webService',
      width: 140,
      getCellValue: (row) => row.webService || '',
      hidden: true,
    },
    {
      title: 'Message on success',
      name: 'messageTemplateId',
      width: 160,
      getCellValue: (row) =>
        row.messageTemplateId && row.messageTemplateId !== 'none'
          ? getLabel(row.messageTemplateId, messageTemplates)
          : '',
      hidden: true,
    },
    {
      title: 'Has query',
      name: 'query',
      width: 110,
      getCellValue: (row) => (row.query ? 'yes' : 'no'),
      hidden: true,
    },
    {
      title: 'Bypass logic on call',
      name: 'bypassLogicOnCall',
      width: 120,
      getCellValue: (row) => (row.bypassLogicOnCall ? 'yes' : 'no'),
      hidden: true,
    },
    {
      title: 'Reasons',
      name: 'reasons',
      width: 100,
      // A comma separated string on the document.
      getCellValue: (row) =>
        row.reasons
          ? String(row.reasons).split(',').filter((reason) => reason.trim())
              .length
          : 0,
      hidden: true,
    },
    {
      title: 'Action',
      name: 'action',
      width: 180,
      align: 'center',
      formatterComponent: ({ row }) => (
        <ul className="list-inline font-size-20 contact-links mb-0">
          {openViewModal && <ViewButton row={row} onPress={openViewModal} />}
          {openEditModal && row.googleSpreadsheetId !== 'processing' && (
            <EditButton row={row} onPress={openEditModal} />
          )}
          {openTestQueryModal && row.query !== '' && (
            <TestQueryButton row={row} onPress={openTestQueryModal} />
          )}
          {row.googleSpreadsheetId &&
            !['new', 'processing', 'error'].includes(
              row.googleSpreadsheetId,
            ) && <GoogleSpreadsheetButton row={row} />}
        </ul>
      ),
      filter: 'disabled',
      sorting: 'disabled',
    },
  ];
};

export default createTableColumns;
